'use client'

import { Card, ProductCard } from '@/components/products/card/product-card'
import { PaginationGrid } from '@/components/utility/pagination/pagination-grid'
import {
  type SectionHeaderProps,
  SectionHeaderTypes,
} from '@/components/utility/section-header'
import { type Breakpoint } from '@/components/utility/size/show-on-size'
import { type AdvertData } from '@/lib/data/products/get-advert-server'
import { type Product } from '@/lib/generated/graphql'
import { useIsWindowBelowBreakpoint } from '@/lib/utils/use-window-size'
import times from 'lodash/times'
import { type ReactNode } from 'react'

export interface ProductGridSkeletonProps {
  title: string
}

const useCountColumns = ({ vertical }: { vertical?: boolean }) => {
  return vertical
    ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5'
    : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'
}

export const ProductGridSkeleton = ({ title }: ProductGridSkeletonProps) => {
  const elements: JSX.Element[] = []
  const isSmall = useIsWindowBelowBreakpoint('sm')

  times(20, () => {
    elements.push(
      <Card
        vertical={!isSmall}
        data-testid='product-card-skeleton'
        className='animate-pulse !bg-gray-200'
      />,
    )
  })

  return (
    <PaginationGrid
      gridClassName={useCountColumns({ vertical: !isSmall })}
      elements={elements}
      title={title ?? ''}
      isSkeleton={true}
      showBottomPagination={false}
      cursor={1}
      totalPages={1}
      itemPerPage={30}
      currentPage={1}
    />
  )
}

const getProductCards = (
  productDataList: Product[],
  advertData: AdvertData[],
  vertical?: boolean,
  overrideWidth?: string | number,
  breakPointForSizeTransition?: Breakpoint,
  eventNameOverride?: string,
  overrideAddToCartEvent?: string,
) =>
  productDataList.map((productData) => {
    const advert = advertData.find(
      (item) => item.advertId === productData.product_advertLookUp,
    )
    const available = advert?.variantAvailable ?? false
    return (
      <ProductCard
        key={productData.product_id}
        overrideWidth={overrideWidth}
        breakPointForSizeTransition={breakPointForSizeTransition}
        vertical={vertical}
        product={productData}
        eventNameOverride={eventNameOverride}
        overrideAddToCartEvent={overrideAddToCartEvent}
        available={available}
        advert={advert as AdvertData}
      />
    )
  })

interface ProductPaginationGridProps {
  title: string
  showBottomPagination: boolean
  itemPerPage: number
  total: number
  pageParamAsNumber: number
  headerProps?: SectionHeaderProps
  seeAllLink: string
  footer: ReactNode
  productDataList: Product[]
  searchParams?: Record<string, string>
  breakPointForSizeTransition?: Breakpoint
  advertData: AdvertData[]
}

export const ProductPaginationGrid = ({
  productDataList,
  title,
  showBottomPagination = false,
  itemPerPage = 30,
  total,
  pageParamAsNumber,
  headerProps,
  seeAllLink,
  footer,
  searchParams,
  breakPointForSizeTransition,
  advertData,
}: ProductPaginationGridProps) => {
  const isSmall = useIsWindowBelowBreakpoint('sm')
  const cards = getProductCards(
    productDataList.slice(0, itemPerPage),
    advertData,
    !isSmall,
    undefined,
    breakPointForSizeTransition,
  )

  const newColumns = useCountColumns({ vertical: !isSmall })

  return (
    <>
      <PaginationGrid
        elements={cards}
        title={title ?? ''}
        isSkeleton={false}
        showBottomPagination={showBottomPagination}
        gridClassName={newColumns}
        cursor={pageParamAsNumber}
        totalPages={Math.ceil(total / itemPerPage)}
        itemPerPage={itemPerPage}
        currentPage={pageParamAsNumber}
        headerProps={
          total === 0
            ? ({
                title: headerProps?.title ?? '',
                type: SectionHeaderTypes.empty,
              } as SectionHeaderProps)
            : ({ ...headerProps, seeAllLink } as SectionHeaderProps)
        }
        searchParams={searchParams}
      />
      {footer}
    </>
  )
}
